import React, { FunctionComponent, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../store/store';
import { getCreditLimitIncreaseResultContent } from '../../store/contentSlice';
import {
  getCliAccountId,
  getCliSuccessOrErrorStatus,
  getCliResponseCodeStatus,
  getNewCreditLimit,
  getUpdatedAvailableBalance,
  getCliApprovedState,
} from '../../store/creditLimitIncreaseSlice';
import {
  CREDIT_INCREASE_DENIED,
  CREDIT_INCREASE_PARTIAL_SUCCESS,
  CREDIT_INCREASE_SUCCESS,
  CREDIT_INCREASE_UNDER_REVIEW,
} from '../../utils/analytics/analyticsEvents';
import { updateCreditLimit } from '../../store/accountListSlice';
import {
  formatAmount,
  setImagePath,
  detectDeviceType,
} from '../../utils/helpers';
import {
  capturePageLoadAnalytics,
  captureClickAnalyticsObject,
} from '../../utils/analytics/analyticsDispatchers';
import './creditLimitIncrease.css';

const CreditLimitIncreaseResult: FunctionComponent = (props: any) => {
  const { displayName, last4Accountnumber } = props;
  const isFullApproval = useSelector(getCliApprovedState);
  const isCliFailureResponse = useSelector(getCliSuccessOrErrorStatus);
  const cliResponseCode = useSelector(getCliResponseCodeStatus);
  const accountId = useSelector(getCliAccountId);
  const updatedAvailableBalance = useSelector(getUpdatedAvailableBalance);
  const newCreditLimit = useSelector(getNewCreditLimit);
  const {
    cliImmediateUseText,
    cliExplanationLetterText,
    newCreditLimitText,
    usDollarSymbol,
    approvedHeading,
    partiallyApprovedHeading,
    reviewHeading,
    declinedHeading,
  }: any = useSelector(getCreditLimitIncreaseResultContent);
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (!isCliFailureResponse) {
      dispatch(
        updateCreditLimit({
          accountId,
          updatedAvailableBalance,
          newCreditLimit,
        }),
      );
    }
  }, [isCliFailureResponse]);

  let cliResultHeading = '';

  if (isFullApproval) {
    cliResultHeading = approvedHeading;
  } else {
    cliResultHeading = partiallyApprovedHeading;
  }

  let cliFailedHeading = '';

  if (cliResponseCode) {
    cliFailedHeading = reviewHeading;
  } else {
    cliFailedHeading = declinedHeading;
  }

  const captureCliAnalytics = ({
    dataChannel,
    typeOfResult,
  }: {
    dataChannel: string;
    typeOfResult: boolean;
  }) => {
    // calling button click analytics in cli for both fullApproval and Partial scenarios
    if (typeOfResult) {
      captureClickAnalyticsObject({
        dataType: detectDeviceType,
        dataReason: `requestIncrease:${displayName}`,
        dataObject: 'button',
        dataChannel,
      });
      capturePageLoadAnalytics(
        dataChannel === 'Error'
          ? CREDIT_INCREASE_UNDER_REVIEW
          : CREDIT_INCREASE_SUCCESS,
      );
    } else {
      captureClickAnalyticsObject({
        dataType: detectDeviceType,
        dataReason: `requestIncrease:${displayName}`,
        dataObject: 'button',
        dataChannel,
      });
      capturePageLoadAnalytics(
        dataChannel === 'Error'
          ? CREDIT_INCREASE_DENIED
          : CREDIT_INCREASE_PARTIAL_SUCCESS,
      );
    }
  };
  useEffect(() => {
    if (isCliFailureResponse) {
      captureCliAnalytics({
        dataChannel: 'Error',
        typeOfResult: cliResponseCode,
      });
    } else {
      captureCliAnalytics({
        dataChannel: 'Success',
        typeOfResult: isFullApproval,
      });
    }
  }, [isCliFailureResponse, cliResponseCode, isFullApproval]);

  return (
    <div className="cli-response-wrapper">
      {isCliFailureResponse && (
        <div className="cli-under-review-wrapper">
          <div className="under-review-header">{cliFailedHeading}</div>
          <div className="cli-msg-content">{cliExplanationLetterText}</div>
        </div>
      )}

      {!isCliFailureResponse && (
        <div className="cli-partially-approved-response-wrapper">
          <div className="cli-partially-approved-response-head">
            <div className="cli-partially-approved-img">
              <img src={setImagePath('cli-approved')} />
            </div>
            <div className="cli-partially-approved-title">
              <span>{cliResultHeading}</span>
            </div>
            <div className="cli-client-name">
              {displayName} *{last4Accountnumber}
            </div>
            <div className="cli-partially-approved-updated-Limit">
              <div className="cli-partially-approved-updated-current-limit">
                {usDollarSymbol}
                {formatAmount(newCreditLimit)}
              </div>
              <div className="cli-partially-approved-updated-current-limit-text">
                {newCreditLimitText}
              </div>
            </div>
          </div>
          <div className="cli-partially-approved-response-message-wrapper">
            <div className="cli-response-immediate-use-text">
              {cliImmediateUseText}
            </div>
            {!isFullApproval && (
              <div className="cli-partially-approved-explanation-text">
                {cliExplanationLetterText}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CreditLimitIncreaseResult;
